import React from 'react';
import PropTypes from 'prop-types';
import { DataTypeProvider } from '@devexpress/dx-react-grid';

import Chip from '@mui/material/Chip';

import { priorityColors } from '../helper/helper';

// ---------------------------------------------------------------------------
// Status chip colours – date-aware
//
// Priority order (highest wins):
//   1. Overdue (past due date)          → Red
//   2. Due today / within 2 days        → Amber
//   3. Status-based colour              → see STATUS_CHIP_COLORS
//
// The date override only applies to non-terminal statuses (Complete, Cancelled
// are already done/closed so their colour never changes regardless of due date).
// ---------------------------------------------------------------------------

const STATUS_CHIP_COLORS = {
  Assigned: '#2196f3', // Blue
  Pending: '#9e9e9e', // Grey
  Draft: '#9e9e9e', // Grey
  'Submitted for Approval': '#9c27b0',
  Complete: '#4caf50', // Green
  'On Hold': '#795548',
  Cancelled: '#607d8b'
};

// eslint-disable-next-line no-undef
const TERMINAL_STATUSES = new Set(['Complete', 'Cancelled']);

const RED = '#f44336'; // overdue
const AMBER = '#ff9800'; // due today / within 2 days

/**
 * Returns the background colour for the status chip.
 * `dueDate` should be a raw ISO-8601 string (or anything parseable by Date).
 */
function getStatusChipColor(status, dueDate) {
  if (!TERMINAL_STATUSES.has(status) && dueDate) {
    const due = new Date(dueDate);
    if (!Number.isNaN(due.getTime())) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      // diffDays < 0  → past due  |  0-2 → due today / soon
      const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
      if (diffDays < 0) return RED;
      if (diffDays <= 2) return AMBER;
    }
  }
  return STATUS_CHIP_COLORS[status] || '#9e9e9e';
}

// ---------------------------------------------------------------------------
// Status chip
// ---------------------------------------------------------------------------
const TaskStatusFormatter = ({ value, row }) => {
  // Both MyTask and TotalTask rows carry the raw document at row._raw
  const rawDueDate = row?._raw?.dueDate;
  return (
    <Chip
      label={value || '—'}
      size="small"
      style={{
        backgroundColor: getStatusChipColor(value, rawDueDate),
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.7rem'
      }}
    />
  );
};
TaskStatusFormatter.propTypes = { value: PropTypes.string, row: PropTypes.object };

export default function TaskStatusProvider() {
  return <DataTypeProvider for={['status']} formatterComponent={TaskStatusFormatter} />;
}

// ---------------------------------------------------------------------------
// Priority chip
// ---------------------------------------------------------------------------
const TaskPriorityFormatter = ({ value }) => (
  <Chip
    label={value || '—'}
    size="small"
    variant="outlined"
    style={{
      borderColor: priorityColors[value]?.color || '#757575',
      color: priorityColors[value]?.color || '#757575',
      fontWeight: 600,
      fontSize: '0.7rem'
    }}
  />
);
TaskPriorityFormatter.propTypes = { value: PropTypes.string };

export function TaskPriorityProvider() {
  return <DataTypeProvider for={['priority']} formatterComponent={TaskPriorityFormatter} />;
}
